import { useEffect, useMemo, useRef } from 'react';

/**
 * Spreads push-triggered reloads over a random delay and folds bursts into one,
 * for tenant-wide pushes that every open tab receives at the same moment (the
 * topbar badges, the bell): without it, ~20 tabs re-fetch in the same second.
 *  - jitter: a call schedules one load after a random 0–`maxJitterMs` delay;
 *  - coalesced: calls while a load is scheduled join it, and the window does
 *    not restart, so a steady stream still loads once per window;
 *  - single-flight: a call during an in-flight load schedules exactly one more
 *    load after it finishes, since that response may predate the change.
 *
 * Always runs the latest `load` passed in. For background resyncs only:
 * user-initiated loads should keep calling `load` directly so they stay
 * instant. See useCoalescedReload for a trailing debounce without jitter.
 */
export function useJitteredReload(load: () => Promise<unknown> | void, maxJitterMs = 2000): () => void {
  const loadRef = useRef(load);
  loadRef.current = load;

  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const inFlightRef = useRef(false);
  const dirtyRef = useRef(false);
  const unmountedRef = useRef(false);

  useEffect(() => {
    unmountedRef.current = false;
    return () => {
      unmountedRef.current = true;
      clearTimeout(timerRef.current);
      timerRef.current = undefined;
    };
  }, []);

  return useMemo(() => {
    const schedule = (): void => {
      if (unmountedRef.current) return;
      if (inFlightRef.current) {
        dirtyRef.current = true;
        return;
      }
      if (timerRef.current !== undefined) return;

      timerRef.current = setTimeout(async () => {
        timerRef.current = undefined;
        if (unmountedRef.current) return;
        inFlightRef.current = true;
        try {
          await loadRef.current();
        } catch {
          /* the load reports its own errors */
        } finally {
          inFlightRef.current = false;
          if (dirtyRef.current) {
            dirtyRef.current = false;
            schedule();
          }
        }
      }, Math.random() * maxJitterMs);
    };

    return schedule;
  }, [maxJitterMs]);
}
